<template>
    <transition name="fade">
        <div
            v-if="typeof index === 'number'"
            class="light-gallery"
            @touchend="touchendHandler"
            @touchmove="touchmoveHandler"
            @touchstart="touchstartHandler">
            <div
                :style="`background: ${background}`"
                class="bg-black fixed z-30 inset-0 h-screen w-screen left-0 top-0 overflow-clip block"
                v-on:click="close">
                <div :class="['light-gallery__spinner', !isImageLoaded || 'hide']">
                    <div
                        :style="`border-color: ${interfaceColor}`"
                        class="light-gallery__dot"
                    />
                    <div
                        :style="`border-color: ${interfaceColor}`"
                        class="light-gallery__dot"
                    />
                    <div
                        :style="`border-color: ${interfaceColor}`"
                        class="light-gallery__dot"
                    />
                </div>
                <div class="text-center absolute z-50 block w-full top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
                    <ul class="h-full w-full whitespace-nowrap">
                        <li
                            v-for="(image, imageIndex) in formattedImages"
                            :key="imageIndex"
                            :style="`transform: translate3d(${currentIndex * -100}%, 0px, 0px);`"
                            class="inline-table text-center h-full w-full relative align-middle">
                            <div class="inline-block relative mx-auto my-0 max-w-full max-h-screen">
                                <img
                                    class="max-w-full max-h-screen"
                                    :ref="`lg-img-${imageIndex}`"
                                    :src="shouldPreload(imageIndex) ? image.url : false"
                                    @load="imageLoaded($event, imageIndex)">
                            </div>
                        </li>
                    </ul>
                </div>
                <button
                    v-if="currentIndex > 0"
                    :style="`background: ${background}`"
                    class="absolute block z-50 border-0 p-2 cursor-pointer bg-transparent top-1/2 left-8 align-middle -translate-y-1/2"
                    @click.stop="prev()"
                >
                    <svg
                        height="40"
                        viewBox="0 0 25 40"
                        width="25"
                    >
                        <polyline
                            :stroke="interfaceColor"
                            fill="none"
                            points="19 5 5 20 19 35"
                            stroke-linecap="butt"
                            stroke-linejoin="round"
                            stroke-width="3"
                        />
                    </svg>
                </button>
                <button
                    v-if="currentIndex + 1 < images.length"
                    :style="`background: ${background}`"
                    class="absolute block z-50 border-0 p-2 cursor-pointer bg-transparent top-1/2 right-8 align-middle -translate-y-1/2"
                    @click.stop="next()"
                >
                    <svg
                        height="40"
                        viewBox="0 0 25 40"
                        width="25"
                    >
                        <polyline
                            :stroke="interfaceColor"
                            fill="none"
                            points="6 5 20 20 6 35"
                            stroke-linecap="butt"
                            stroke-linejoin="round"
                            stroke-width="3"
                        />
                    </svg>
                </button>
                <button
                    :style="`background: ${background}`"
                    class="right-10 top-5 absolute z-40"
                    @click="close()">
                    <svg
                        height="30"
                        width="30">
                        <g
                            :stroke="interfaceColor"
                            stroke-width="3">
                            <line
                                x1="5"
                                x2="25"
                                y1="5"
                                y2="25"/>
                            <line
                                x1="5"
                                x2="25"
                                y1="25"
                                y2="5"/>
                        </g>
                    </svg>
                </button>
                
            </div>
        </div>
    </transition>
</template>

<script lang="ts">
    const keyMap = {
        LEFT: 37,
        RIGHT: 39,
        ESC: 27,
    };
    interface Image {
        url: string;
    }
    import {defineComponent} from 'vue';

    export default defineComponent({
        props: {
            images: {
                type: Array as () => string[],
                default: () => [],
            },
            index: {
                type: Number,
                default: 1,
            },
            disableScroll: {
                type: Boolean,
                default: false,
            },
            background: {
                type: String,
                default: 'rgba(0, 0, 0, 0.8)',
            },
            interfaceColor: {
                type: String,
                default: 'rgba(255, 255, 255, 0.8)',
            },
        },
        data() {
            return {
                currentIndex: this.index,
                isImageLoaded: false,
                bodyOverflowStyle: '',
                touch: {
                    count: 0,
                    x: 0,
                    y: 0,
                    multitouch: false,
                    flag: false,
                },
            };
        },
        computed: {
            formattedImages(): Image[] {
                return this.images.map(image =>({url: image}));
            },
        },
        watch: {
            index(val: number) {
                if (!document) return;

                this.currentIndex = val;

                if (this.disableScroll && typeof val === 'number') {
                    document.body.style.overflow = 'hidden';
                } else if (this.disableScroll && !val) {
                    document.body.style.overflow = this.bodyOverflowStyle;
                }
            },
            currentIndex(val) {
                this.setImageLoaded(val);
            },
        },
        mounted() {
            if (!document) return;

            this.bodyOverflowStyle = document.body.style.overflow;
            this.bindEvents();
        },
        unmounted() {
            if (!document) return;

            if (this.disableScroll) {
                document.body.style.overflow = this.bodyOverflowStyle;
            }
            this.unbindEvents();
        },
        methods: {
            close() {
                this.$emit('close');
            },
            prev() {
                if (this.currentIndex === 0) return;
                this.currentIndex -= 1;
                this.$emit('slide', {index: this.currentIndex});
            },
            next() {
                if (this.currentIndex === this.images.length - 1) return;
                this.currentIndex += 1;
                this.$emit('slide', {index: this.currentIndex});
            },
            imageLoaded($event: Event, imageIndex: number) {
                const {target} = $event;
                target.classList.add('opacity-1');

                if (imageIndex === this.currentIndex) {
                    this.setImageLoaded(imageIndex);
                }
            },
            getImageElByIndex(index: number) {
                const elements = this.$refs[`lg-img-${index}`] || [];
                return elements[0];
            },
            setImageLoaded(index: number) {
                const el = this.getImageElByIndex(index);
                this.isImageLoaded = !el ? false : el.classList.contains('opacity-1');
            },
            shouldPreload(index: number) {
                const el = this.getImageElByIndex(index) || {};
                const {src} = el;

                return !!src
                    || index === this.currentIndex
                    || index === this.currentIndex - 1
                    || index === this.currentIndex + 1;
            },
            bindEvents() {
                document.addEventListener('keydown', this.keyDownHandler, false);
            },
            unbindEvents() {
                document.removeEventListener('keydown', this.keyDownHandler, false);
            },
            touchstartHandler(event: Event) {
                this.touch.count += 1;
                if (this.touch.count > 1) {
                    this.touch.multitouch = true;
                }
                this.touch.x = event.changedTouches[0].pageX;
                this.touch.y = event.changedTouches[0].pageY;
            },
            touchmoveHandler(event: Event) {
                if (this.touch.flag || this.touch.multitouch) return;

                const touchEvent = event.touches[0] || event.changedTouches[0];

                if (touchEvent.pageX - this.touch.x > 40) {
                    this.touch.flag = true;
                    this.prev();
                } else if (touchEvent.pageX - this.touch.x < -40) {
                    this.touch.flag = true;
                    this.next();
                }
            },
            touchendHandler() {
                this.touch.count -= 1;
                if (this.touch.count <= 0) {
                    this.touch.multitouch = false;
                }
                this.touch.flag = false;
            },
            keyDownHandler(event: KeyboardEvent) {
                switch (event.keyCode) {
                    case keyMap.LEFT:
                        this.prev();
                        break;
                    case keyMap.RIGHT:
                        this.next();
                        break;
                    case keyMap.ESC:
                        this.close();
                        break;
                    default:
                        break;
                }
            },
        },
    });
</script>
